import type { VerdictTone } from "./fuxi-verdict.js";

/**
 * Where the three 复习 feedback sounds are served from, and what each is called.
 *
 * The one thing the page, the browser bundle and the asset build all have to
 * agree about. `src/assets/feedback-sounds.ts` writes the files under these
 * names, `src/user/fuxi-markup.ts` puts the base on the region, and
 * `src/site/fuxi-sounds.ts` builds an address out of the two.
 *
 * A module of its own so that the bundle can import it. `fuxi-markup.ts` draws
 * a page and pulls most of `/user/` in behind it, and the site bundle loads on
 * every page of the site.
 *
 * **The base is on the region and the rest is here.** Naming all three
 * addresses in the markup would put sixty bytes on every card for two files the
 * reader may never hear, and naming none of them would leave the bundle holding
 * a path this repository already writes in `lib/site/site-uploads.ts`.
 *
 * **A file is named after the verdict it answers, not after the cue it came
 * from.** `uisfx` calls them success, warning and error. What a reader fetches
 * is `right.mp3`, `ok.mp3` or `wrong.mp3`, which is the vocabulary the rest of
 * 复习 uses. See `src/user/fuxi-verdict.ts`.
 */

/** The prefix the sounds are published under. See `lib/site/site-uploads.ts`. */
export const feedbackSoundsBase = "/sounds";

/**
 * What one verdict's sound is published as.
 * @param tone - Which of the three treatments the card was marked in.
 * @returns The file name, without a directory.
 */
export function feedbackSoundFile(tone: VerdictTone): string {
  return `${tone}.mp3`;
}

/**
 * The address of the sound one verdict is heard as.
 * @param base - Where the sounds are served from, as the region gives it.
 * @param tone - Which of the three treatments the card was marked in.
 * @returns The address to play.
 */
export function feedbackSoundAddress(base: string, tone: VerdictTone): string {
  return `${base}/${feedbackSoundFile(tone)}`;
}
